// skills/architecting/architectingUI.js
// Architecting UI rendering and interaction

import { architectingSkill } from './architecting.js';
import { BLUEPRINTS, TOOLS, MILESTONES, CIVIC_CONTRACTS, BLUEPRINT_TIER_NAMES, RARITY_COLORS, getBlueprintsByTier } from './architectingData.js';
import { gameState } from '../../game/gameState.js';
import { SKILLS, GAME_CONFIG } from '../../game/constants.js';
import logger from '../../utils/logger.js';
import { IS_DEV } from '../../config/env.js';
import { adminPanel } from '../admin/admin.js';
import { getCurrentSkillId } from '../../core/routeSetup.js';
import { getItem, getItemName, getItemIcon } from '../../data/items/index.js';
import { formatTime, formatWithCommas, formatNumber } from '../../utils/format.js';
import { toastWarning, toastSuccess } from '../../ui/toast.js';
import { getXPPerHour } from '../../systems/experience.js';
import { getTalentFlatBonus } from '../../systems/talentTree/talentTree.js';
import { renderSkillInfo } from '../../ui/skillInfo.js';
import { renderTalentButton, isTalentTreeOpen } from '../../systems/talentTree/talentTreeUI.js';
import { getSkillIcon, skillRegistry } from '../../core/skillRegistry.js';
import { QUESTS } from '../../data/quests/questData.js';

// UI state
let currentTab = 'blueprints';
let currentBlueprintTier = 1;

/**
 * Find the quest number that provides a specific unlock
 */
function findQuestByUnlock(unlockId) {
    for (const [questNum, quest] of Object.entries(QUESTS)) {
        if (quest.rewards?.unlocks?.includes(unlockId)) {
            return parseInt(questNum);
        }
    }
    return null;
}

export function getCurrentTab() {
    return currentTab;
}

export function switchTab(tabId) {
    currentTab = tabId;
    renderArchitectingUI();
}

export function switchBlueprintTier(tier) {
    currentBlueprintTier = tier;
    renderArchitectingUI();
}

/**
 * Get minimum level required to unlock any blueprint in a tier
 */
function getTierMinLevel(tier) {
    const blueprints = getBlueprintsByTier(tier);
    if (blueprints.length === 0) return 99;
    return Math.min(...blueprints.map(b => b.level));
}

/**
 * Check if tier is fully locked (player below min level for tier)
 */
function isTierLocked(tier, playerLevel) {
    return playerLevel < getTierMinLevel(tier);
}

/**
 * Render compact skill header with integrated tool upgrade (matches mining/woodcutting style)
 */
function renderCompactHeader() {
    const skill = gameState.getSkill(SKILLS.ARCHITECTING);
    const xpPerHour = getXPPerHour(SKILLS.ARCHITECTING);

    // Calculate display values
    const isMax = skill.level >= GAME_CONFIG.MAX_LEVEL;
    const virtualEnabled = gameState.settings.virtualLevelsEnabled;
    const isVirtualMax = skill.virtualLevel >= GAME_CONFIG.VIRTUAL_LEVEL_CAP;

    let levelDisplay, progressPercent, progressText;

    if (isMax && virtualEnabled) {
        levelDisplay = `${skill.level} (${skill.virtualLevel})`;
        progressPercent = isVirtualMax ? 100 : (skill.currentXp / skill.nextLevelXp * 100);
        progressText = isVirtualMax ? 'Virtual Max' : `${formatWithCommas(skill.currentXp)} / ${formatWithCommas(skill.nextLevelXp)}`;
    } else if (isMax) {
        levelDisplay = `${skill.level} (Max)`;
        progressPercent = 100;
        progressText = 'Mastered';
    } else {
        levelDisplay = skill.level;
        progressPercent = (skill.currentXp / skill.nextLevelXp * 100);
        progressText = `${formatWithCommas(skill.currentXp)} / ${formatWithCommas(skill.nextLevelXp)}`;
    }

    // Get tool info
    const tools = Object.values(TOOLS);
    const highestOwned = gameState.getHighestPurchasedTool(SKILLS.ARCHITECTING, tools);
    const activeTool = highestOwned || tools[0];
    const activeMultiplier = activeTool?.speedMultiplier || 1.0;
    const goldCount = gameState.getGold();

    // Find next upgrade
    const allToolsSorted = [...tools].sort((a, b) => a.level - b.level);
    const nextUpgrade = allToolsSorted.find(tool =>
        tool.price > 0 && !gameState.hasPurchasedTool(SKILLS.ARCHITECTING, tool.id)
    );

    // Build upgrade HTML
    let upgradeHtml = '';
    if (nextUpgrade) {
        const isLocked = skill.level < nextUpgrade.level;
        const canAfford = goldCount >= nextUpgrade.price;

        if (isLocked) {
            upgradeHtml = `
                <span class="compact-upgrade locked">
                    &rarr; ${nextUpgrade.name} (${nextUpgrade.speedMultiplier}x)
                    <span class="upgrade-cost ${canAfford ? 'affordable' : 'cant-afford'}" data-price="${nextUpgrade.price}">${getItemIcon('coins')} ${formatNumber(nextUpgrade.price)}</span>
                    <span class="upgrade-lock">&#x1F512; ${skill.level}/${nextUpgrade.level}</span>
                </span>
            `;
        } else {
            upgradeHtml = `
                <span class="compact-upgrade ${canAfford ? 'available' : ''}"
                      onclick="window.architectingUI.upgradeTool('${nextUpgrade.id}')">
                    &rarr; ${nextUpgrade.name} (${nextUpgrade.speedMultiplier}x)
                    <span class="upgrade-cost ${canAfford ? 'affordable' : 'cant-afford'}" data-price="${nextUpgrade.price}">${getItemIcon('coins')} ${formatNumber(nextUpgrade.price)}</span>
                </span>
            `;
        }
    } else {
        upgradeHtml = `<span class="compact-upgrade maxed">&#x2713; All hammers</span>`;
    }

    return `
        <div class="compact-header">
            <div class="compact-header-row">
                <div class="compact-title">
                    <span class="compact-icon">${getSkillIcon(SKILLS.ARCHITECTING)}</span>
                    <span class="compact-name">Architecting</span>
                    ${renderSkillInfo(SKILLS.ARCHITECTING)}
                    ${renderTalentButton(SKILLS.ARCHITECTING)}
                    <span class="compact-level">Lv ${levelDisplay}</span>
                </div>
                <div class="compact-stats">
                    <span class="compact-stat xp-total">
                        <span class="stat-label">Total XP:</span>
                        <span class="stat-value">${formatWithCommas(skill.xp)}</span>
                    </span>
                    <span class="compact-stat xp-rate">
                        <span class="stat-label">XP/h:</span>
                        <span class="stat-value">${formatWithCommas(xpPerHour)}</span>
                    </span>
                </div>
                <div class="compact-tool">
                    <span class="tool-label">Hammer:</span>
                    <span class="tool-current">${activeTool.name} (${activeMultiplier}x)</span>
                    ${upgradeHtml}
                </div>
            </div>
            <div class="compact-progress">
                <div class="compact-progress-bar">
                    <div class="compact-progress-fill" style="width: ${progressPercent}%"></div>
                    <span class="compact-progress-percent">${Math.floor(progressPercent)}%</span>
                </div>
                <span class="compact-progress-text">${progressText} XP</span>
            </div>
        </div>
    `;
}

export function renderArchitectingUI(params = {}) {
    const container = document.getElementById('skill-content');
    const skill = gameState.getSkill(SKILLS.ARCHITECTING);

    // Use params tab if provided, otherwise use persisted state
    const activeTab = params.tab || currentTab;
    currentTab = activeTab;

    container.innerHTML = `
    <div class="architecting-interface">
      ${renderCompactHeader()}

      <div class="skill-tabs">
        <button class="skill-tab ${activeTab === 'blueprints' ? 'active' : ''}"
                onclick="window.architectingUI.switchTab('blueprints')">
          Blueprints
        </button>
        <button class="skill-tab ${activeTab === 'milestones' ? 'active' : ''}"
                onclick="window.architectingUI.switchTab('milestones')">
          Milestones
        </button>
      </div>

      <div class="architecting-content">
        ${activeTab === 'blueprints' ? renderBlueprintsTab(skill) : ''}
        ${activeTab === 'milestones' ? renderMilestonesTab(skill) : ''}
      </div>
    </div>
  `;
}

function renderBlueprintsTab(skill) {
    const hasContractor = architectingSkill.hasContractor();

    if (!hasContractor) {
        return renderPreContractorBlueprints(skill);
    }

    return renderPostContractorBlueprints(skill);
}

/**
 * Render the contractor status indicator
 */
function renderContractorStatus() {
    const activeBlueprints = gameState.getActiveBlueprints();
    const activeMilestone = gameState.getActiveMilestone();
    const maxSlots = architectingSkill.getContractorSlotCount();
    const usedSlots = architectingSkill.getUsedContractorSlots();

    let statusText = 'Idle';
    const tasks = [];

    for (const ab of activeBlueprints) {
        const bp = Object.values(BLUEPRINTS).find(b => b.id === ab.blueprintId);
        if (bp) tasks.push(bp.name);
    }

    if (activeMilestone) {
        const ms = Object.values(MILESTONES).find(m => m.id === activeMilestone.milestoneId);
        if (ms) tasks.push(ms.name);
    }

    const activeCivic = gameState.getActiveCivicContract();
    if (activeCivic && activeCivic.status === 'building') {
        const cc = Object.values(CIVIC_CONTRACTS).find(c => c.id === activeCivic.contractId);
        if (cc) tasks.push(cc.name);
    }

    if (tasks.length > 0) {
        statusText = tasks.join(' + ');
    }

    const slotLabel = maxSlots > 1 ? `${usedSlots}/${maxSlots} busy` : '';

    return `
        <div class="contractor-status">
            <span class="contractor-label">Contractor:</span>
            <span class="contractor-task">${statusText}</span>
            ${slotLabel ? `<span class="contractor-slots">(${slotLabel})</span>` : ''}
        </div>
    `;
}

/**
 * Pre-contractor view: only Wooden Chair as active build
 */
function renderPreContractorBlueprints(skill) {
    const questNum = findQuestByUnlock('contractor_hired');
    const questLink = questNum
        ? `<span class="quest-link" onclick="window.questModal.navigateToQuest(${questNum})">The Carpenter's Arrival</span>`
        : 'The Carpenter\'s Arrival';

    return `
        <div class="blueprints-section">
            <div class="pre-contractor-section">
                ${renderQuickBuildCard()}
            </div>

            <div class="contractor-locked-banner">
                <span class="lock-icon">&#x1F512;</span>
                <div class="lock-text">
                    <span class="lock-title">Contractor Required</span>
                    <span class="lock-desc">Reach Architecting level 5 and complete ${questLink} to hire a contractor who builds commissions while you do other things.</span>
                </div>
            </div>

            <div class="locked-blueprints-preview">
                ${renderLockedBlueprintPreview(skill)}
            </div>
        </div>
    `;
}

/**
 * Render greyed out preview of locked blueprints
 */
function renderLockedBlueprintPreview(skill) {
    // Show a few tier 1 blueprints as locked previews — minimal cards
    const blueprints = getBlueprintsByTier(1).filter(b => b.buildMode !== 'active').slice(0, 4);

    return blueprints.map(blueprint => `
        <div class="blueprint-card locked contractor-locked" style="--blueprint-color: ${blueprint.color}">
            <div class="blueprint-card-header">
                <span class="blueprint-card-icon">📐</span>
                <span class="blueprint-card-name">${blueprint.name}</span>
            </div>
            <div class="blueprint-action">
                <span class="locked-text">&#x1F512; Requires Contractor</span>
            </div>
        </div>
    `).join('');
}

/**
 * Post-contractor view: full blueprint grid (existing behavior + contractor status)
 */
function renderPostContractorBlueprints(skill) {
    const tiers = [1, 2, 3, 4, 5];
    const availableContracts = gameState.getAvailableCivicContracts();
    const activeContract = gameState.getActiveCivicContract();
    const hasAutoQueue = getTalentFlatBonus(SKILLS.ARCHITECTING, 'autoQueueBlueprint') > 0;
    const hasDualTalent = getTalentFlatBonus(SKILLS.ARCHITECTING, 'dualBlueprint') > 0;
    const autoQueueEnabled = gameState.getAutoQueueEnabled();

    // Show toggles only when relevant talents are unlocked
    const togglesHtml = hasAutoQueue ? `
        <div class="blueprint-toggles">
            <button class="toggle-btn ${autoQueueEnabled ? 'active' : ''}"
                    onclick="window.architectingUI.toggleAutoQueue()">
                Auto-Queue: ${autoQueueEnabled ? 'ON' : 'OFF'}
            </button>
            ${hasDualTalent ? `<span class="dual-slot-label">Apprentice Contractor: 2 slots</span>` : ''}
        </div>
    ` : '';

    return `
        <div class="blueprints-section">
            ${renderContractorStatus()}

            ${availableContracts.length > 0 || activeContract ? renderCivicContractsSection(skill, availableContracts, activeContract) : ''}

            ${togglesHtml}

            <div class="tier-tabs">
                ${tiers.map(tier => {
                    const isLocked = isTierLocked(tier, skill.level);
                    const minLevel = getTierMinLevel(tier);
                    return `
                        <button class="tier-tab ${currentBlueprintTier === tier ? 'active' : ''} ${isLocked ? 'locked' : ''}"
                                onclick="window.architectingUI.switchBlueprintTier(${tier})">
                            ${BLUEPRINT_TIER_NAMES[tier]}
                            ${isLocked ? `<span class="tier-lock">Lv ${minLevel}</span>` : ''}
                        </button>
                    `;
                }).join('')}
            </div>

            <div class="blueprint-grid">
                ${currentBlueprintTier === 1 ? renderQuickBuildCard() : ''}
                ${renderBlueprintCards(skill, currentBlueprintTier)}
            </div>
        </div>
    `;
}

/**
 * Render the Rickety Chair quick build card (available pre and post contractor)
 */
function renderQuickBuildCard() {
    const woodenChair = BLUEPRINTS.WOODEN_CHAIR;
    if (!woodenChair) return '';

    const currentAction = gameState.player.currentAction;
    const isQuickBuilding = currentAction?.type === 'architecting' && currentAction?.buildMode === 'active';
    const materialStatus = getMaterialStatus(woodenChair);

    return `
        <div class="quick-build-card ${isQuickBuilding ? 'active' : ''}"
             data-blueprint-id="${woodenChair.id}">
            <div class="blueprint-card-header">
                <span class="blueprint-card-level">Lvl ${woodenChair.level}</span>
                <span class="blueprint-card-icon">📐</span>
                <span class="blueprint-card-name">${woodenChair.name}</span>
            </div>

            <div class="blueprint-card-info">
                <div class="blueprint-stat">
                    <span class="stat-icon architecting-icon"></span>
                    <span class="stat-label">XP:</span>
                    <span class="stat-value xp">${woodenChair.xp}</span>
                </div>
                <div class="blueprint-stat">
                    <span class="stat-icon time-icon"></span>
                    <span class="stat-label">Time:</span>
                    <span class="stat-value">${formatTime(woodenChair.baseTime / architectingSkill.getEffectiveSpeedMultiplier() / 1000)}</span>
                </div>
            </div>

            <div class="blueprint-materials">
                <div class="materials-label">Materials</div>
                ${materialStatus.map(m => `
                    <div class="material-row ${m.hasEnough ? '' : 'missing'}">
                        <span class="material-icon">${m.icon}</span>
                        <span class="material-name">${m.name}</span>
                        <span class="material-count">${m.playerQty}/${m.required}</span>
                    </div>
                `).join('')}
            </div>

            <div class="blueprint-action">
                <button class="action-btn ${isQuickBuilding ? 'active' : ''}"
                        onclick="${isQuickBuilding ? `window.architectingUI.stopQuickBuild()` : `window.architectingUI.startQuickBuild('${woodenChair.id}')`}">
                    ${isQuickBuilding ? 'Stop' : 'Build'}
                </button>
            </div>

            ${isQuickBuilding ? `
                <div class="blueprint-progress">
                    <div class="blueprint-progress-fill" style="width: ${(currentAction.progress || 0) * 100}%"></div>
                </div>
            ` : ''}
        </div>
    `;
}

function renderCivicContractsSection(skill, availableContracts, activeContract) {
    const activeBlueprint = gameState.getActiveBlueprint();
    const parallelNote = activeBlueprint || activeContract ? `
        <div class="parallel-activity-note">
            <span class="parallel-icon">🔄</span>
            <span class="parallel-text">Civic contracts run alongside regular blueprints</span>
        </div>
    ` : '';

    return `
        <div class="civic-contracts-section">
            <div class="civic-header">
                <span class="civic-title">Civic Contracts</span>
                <span class="civic-subtitle">Rare multi-phase projects with valuable rewards</span>
                ${parallelNote}
            </div>

            ${activeContract ? renderActiveCivicContract(activeContract) : ''}

            ${availableContracts.length > 0 && !activeContract ? `
                <div class="available-contracts">
                    ${availableContracts.map(contractId => {
                        const contract = Object.values(CIVIC_CONTRACTS).find(c => c.id === contractId);
                        if (!contract) return '';
                        return renderAvailableCivicContract(contract, skill);
                    }).join('')}
                </div>
            ` : ''}
        </div>
    `;
}

function renderActiveCivicContract(activeContract) {
    const contract = Object.values(CIVIC_CONTRACTS).find(c => c.id === activeContract.contractId);
    if (!contract) return '';

    const currentPhase = contract.phases[activeContract.currentPhase];
    const elapsed = Date.now() - activeContract.phaseStartedAt;
    const progress = Math.min(elapsed / currentPhase.duration, 1);
    const isReady = activeContract.status === 'ready_for_next';

    // Current phase XP display
    const currentXpHtml = `
        <div class="phase-xp-reward current">
            <span class="xp-label">Phase XP:</span>
            <span class="xp-value ${currentPhase.xp > 0 ? '' : 'none'}">${currentPhase.xp > 0 ? `+${formatWithCommas(currentPhase.xp)}` : 'None'}</span>
        </div>
    `;

    // Build next phase preview (always show if there's a next phase)
    let nextPhaseHtml = '';
    const nextPhaseIndex = activeContract.currentPhase + 1;
    if (nextPhaseIndex < contract.phases.length) {
        const nextPhase = contract.phases[nextPhaseIndex];

        // Check gold requirement
        const playerGold = gameState.getGold();
        const canAffordGold = nextPhase.gold <= 0 || playerGold >= nextPhase.gold;

        // Check material requirements
        const materialStatus = [];
        for (const [itemId, qty] of Object.entries(nextPhase.materials || {})) {
            const item = getItem(itemId);
            const playerQty = gameState.getItemCount(itemId);
            materialStatus.push({
                name: item?.name || itemId,
                icon: getItemIcon(itemId),
                required: qty,
                playerQty,
                hasEnough: playerQty >= qty
            });
        }
        const canAffordMaterials = materialStatus.every(m => m.hasEnough);
        const nextPhaseNeedsSlot = nextPhase.type === 'build';
        const slotsAvailable = !nextPhaseNeedsSlot || architectingSkill.getUsedContractorSlots() < architectingSkill.getContractorSlotCount();
        const canStart = canAffordGold && canAffordMaterials && slotsAvailable;

        // Show costs if there are any
        const hasCosts = nextPhase.gold > 0 || materialStatus.length > 0;
        const costsHtml = hasCosts ? `
            <div class="phase-costs">
                ${nextPhase.gold > 0 ? `
                    <span class="phase-cost ${canAffordGold ? '' : 'missing'}">
                        ${getItemIcon('coins')} ${formatWithCommas(playerGold)}/${formatWithCommas(nextPhase.gold)}
                    </span>
                ` : ''}
                ${materialStatus.map(m => `
                    <span class="phase-cost ${m.hasEnough ? '' : 'missing'}" data-tooltip="${m.name}">
                        ${m.icon} ${m.playerQty}/${m.required}
                    </span>
                `).join('')}
            </div>
        ` : '';

        // Show XP reward for next phase
        const nextXpHtml = `
            <div class="phase-xp-reward">
                <span class="xp-label">XP:</span>
                <span class="xp-value ${nextPhase.xp > 0 ? '' : 'none'}">${nextPhase.xp > 0 ? `+${formatWithCommas(nextPhase.xp)}` : 'None'}</span>
            </div>
        `;

        nextPhaseHtml = `
            <div class="next-phase-requirements ${isReady ? 'ready' : 'preview'}">
                <span class="next-phase-label">${isReady ? 'Next' : 'Up Next'}: ${nextPhase.name} (${nextPhase.type})</span>
                ${costsHtml}
                ${nextXpHtml}
            </div>
            ${isReady ? `
                <button class="civic-next-phase-btn ${canStart ? '' : 'disabled'}"
                        onclick="window.architectingUI.startNextPhase()"
                        ${canStart ? '' : 'disabled'}>
                    Start Next Phase
                </button>
            ` : ''}
        `;
    }

    return `
        <div class="active-civic-contract" style="border-color: ${RARITY_COLORS[contract.rarity]}">
            <div class="civic-contract-header">
                <span class="contract-icon">${contract.icon}</span>
                <span class="contract-name">${contract.name}</span>
                <span class="contract-rarity" style="color: ${RARITY_COLORS[contract.rarity]}">${contract.rarity.toUpperCase()}</span>
            </div>
            <div class="civic-contract-phase">
                <span class="phase-label">Phase ${activeContract.currentPhase + 1}/${contract.phases.length}:</span>
                <span class="phase-name">${currentPhase.name}</span>
                <span class="phase-type">(${currentPhase.type})</span>
            </div>
            <div class="civic-progress">
                <div class="progress-bar">
                    <div class="progress-fill" style="width: ${progress * 100}%; background: ${RARITY_COLORS[contract.rarity]}"></div>
                </div>
                <span class="time-remaining">${formatTime((1 - progress) * currentPhase.duration / 1000)}</span>
            </div>
            ${currentXpHtml}
            ${nextPhaseHtml}
        </div>
    `;
}

function renderAvailableCivicContract(contract, skill) {
    const firstPhase = contract.phases[0];

    // Check requirements
    const canAffordGold = firstPhase.gold <= 0 || gameState.getGold() >= firstPhase.gold;
    const materialStatus = [];
    for (const [itemId, qty] of Object.entries(firstPhase.materials)) {
        const item = getItem(itemId);
        const playerQty = gameState.getItemCount(itemId);
        materialStatus.push({
            name: item?.name || itemId,
            icon: getItemIcon(itemId) || '&#x1F4E6;',
            required: qty,
            playerQty,
            hasEnough: playerQty >= qty
        });
    }
    const canAffordMaterials = materialStatus.every(m => m.hasEnough);
    const slotsAvailable = architectingSkill.getUsedContractorSlots() < architectingSkill.getContractorSlotCount();
    const canStart = canAffordGold && canAffordMaterials && slotsAvailable;

    // Calculate total time and XP
    const totalTime = contract.phases.reduce((sum, p) => sum + p.duration, 0);
    const totalXP = contract.phases.reduce((sum, p) => sum + p.xp, 0) + contract.rewards.xp;

    return `
        <div class="civic-contract-card" style="border-color: ${RARITY_COLORS[contract.rarity]}">
            <div class="civic-contract-header">
                <span class="contract-icon">${contract.icon}</span>
                <div class="contract-title-row">
                    <span class="contract-name">${contract.name}</span>
                    <span class="contract-rarity" style="color: ${RARITY_COLORS[contract.rarity]}">${contract.rarity.toUpperCase()}</span>
                </div>
            </div>
            <div class="contract-desc">${contract.description}</div>

            <div class="contract-phases-preview">
                <span class="phases-label">${contract.phases.length} phases</span>
                <span class="phases-time">~${formatTime(totalTime / 1000)}</span>
            </div>

            <div class="contract-first-phase">
                <span class="first-phase-label">Phase 1 requires:</span>
                <div class="phase-materials">
                    ${materialStatus.map(m => `
                        <span class="phase-mat ${m.hasEnough ? '' : 'missing'}">
                            ${m.name}: ${m.playerQty}/${m.required}
                        </span>
                    `).join('')}
                    ${firstPhase.gold > 0 ? `
                        <span class="phase-mat ${canAffordGold ? '' : 'missing'}">
                            Gold: ${formatWithCommas(gameState.getGold())}/${formatWithCommas(firstPhase.gold)}
                        </span>
                    ` : ''}
                </div>
            </div>

            <div class="contract-rewards">
                <span class="rewards-label">Rewards:</span>
                <span class="reward-xp">${formatWithCommas(totalXP)} XP</span>
                <span class="reward-gold">${getItemIcon('coins')} ${formatWithCommas(contract.rewards.gold)}</span>
            </div>

            <button class="civic-start-btn ${canStart ? '' : 'disabled'}"
                    onclick="window.architectingUI.startCivicContract('${contract.id}')"
                    ${canStart ? '' : 'disabled'}>
                Accept Contract
            </button>
        </div>
    `;
}

function renderBlueprintCards(skill, tier) {
    // Filter out active-mode blueprints (Rickety Chair) - they use the quick build system
    const blueprints = getBlueprintsByTier(tier).filter(b => b.buildMode !== 'active');
    const activeBlueprints = gameState.getActiveBlueprints();
    const hasDualTalent = getTalentFlatBonus(SKILLS.ARCHITECTING, 'dualBlueprint') > 0;

    return blueprints.map(blueprint => {
        const isLocked = skill.level < blueprint.level;

        // Check if this blueprint is in any active slot
        const activeSlotIndex = activeBlueprints.findIndex(ab => ab.blueprintId === blueprint.id);
        const isActive = activeSlotIndex >= 0;

        // Get material status
        const materialStatus = getMaterialStatus(blueprint);
        const hasAllMaterials = materialStatus.every(m => m.hasEnough);

        // Calculate progress for active blueprint
        const progress = isActive ? gameState.getBlueprintProgress(activeSlotIndex) : 0;
        const activeData = isActive ? activeBlueprints[activeSlotIndex] : null;
        const timeRemaining = isActive ? Math.max(0, (1 - progress) * activeData.duration) : 0;

        // Calculate effective time (show penalized time if dual is active)
        const isDual = hasDualTalent && activeBlueprints.length === 2;
        const dualPenalty = isDual ? 0.75 : 1;
        const effectiveTime = blueprint.baseTime / (architectingSkill.getActiveBuff().speedMultiplier * (1 + architectingSkill.getBlueprintSpeedBonus()) * dualPenalty);

        // Slot indicator for dual blueprints
        const slotLabel = isActive && hasDualTalent ? `<span class="slot-indicator">Slot ${activeSlotIndex + 1}</span>` : '';

        const cardClasses = [
            'blueprint-card',
            isActive ? 'active' : '',
            isLocked ? 'locked' : '',
            !hasAllMaterials && !isLocked ? 'no-materials' : ''
        ].filter(Boolean).join(' ');

        return `
            <div class="${cardClasses}"
                 data-blueprint-id="${blueprint.id}"
                 data-slot-index="${activeSlotIndex}"
                 style="--blueprint-color: ${blueprint.color}">

                <div class="blueprint-card-header">
                    <span class="blueprint-card-level">${isLocked ? `Req: ${blueprint.level}` : `Lvl ${blueprint.level}`}</span>
                    <span class="blueprint-card-icon">📐</span>
                    <span class="blueprint-card-name">${blueprint.name}</span>
                    ${slotLabel}
                </div>

                <div class="blueprint-card-info">
                    <div class="blueprint-stat">
                        <span class="stat-icon architecting-icon"></span>
                        <span class="stat-label">XP:</span>
                        <span class="stat-value xp">${blueprint.xp}</span>
                    </div>
                    <div class="blueprint-stat">
                        <span class="stat-icon time-icon"></span>
                        <span class="stat-label">Time:</span>
                        <span class="stat-value">${formatTime(effectiveTime / 1000)}</span>
                    </div>
                </div>

                <div class="blueprint-materials">
                    <div class="materials-label">Materials</div>
                    ${materialStatus.map(m => `
                        <div class="material-row ${m.hasEnough ? '' : 'missing'}">
                            <span class="material-icon">${m.icon}</span>
                            <span class="material-name">${m.name}</span>
                            <span class="material-count">${m.playerQty}/${m.required}</span>
                        </div>
                    `).join('')}
                    ${blueprint.gold > 0 ? `
                        <div class="material-row ${gameState.getGold() >= blueprint.gold ? '' : 'missing'}">
                            <span class="material-icon">${getItemIcon('coins')}</span>
                            <span class="material-name">Gold</span>
                            <span class="material-count">${formatWithCommas(gameState.getGold())}/${formatWithCommas(blueprint.gold)}</span>
                        </div>
                    ` : ''}
                </div>

                <div class="blueprint-client">
                    <span class="client-label">Client:</span>
                    <span class="client-value">${blueprint.townsperson}</span>
                </div>

                <div class="blueprint-action">
                    ${!isLocked ? `
                        <button class="action-btn ${isActive ? 'active' : ''}"
                                onclick="window.architectingUI.toggleBlueprint('${blueprint.id}')">
                            ${isActive ? 'Stop' : 'Build'}
                        </button>
                    ` : `
                        <span class="locked-text">🔒 Locked</span>
                    `}
                </div>

                ${isActive ? `
                    <div class="blueprint-timer">
                        <span class="time-remaining">${formatTime(timeRemaining / 1000)}</span>
                    </div>
                ` : ''}

                <div class="blueprint-progress">
                    <div class="blueprint-progress-fill" style="width: ${progress * 100}%"></div>
                </div>
            </div>
        `;
    }).join('');
}

function getMaterialStatus(blueprint) {
    const materials = [];
    for (const [itemId, qty] of Object.entries(blueprint.materials)) {
        const item = getItem(itemId);
        const playerQty = gameState.getItemCount(itemId);
        materials.push({
            itemId,
            name: item?.name || itemId,
            icon: getItemIcon(itemId) || '&#x1F4E6;',
            required: qty,
            playerQty,
            hasEnough: playerQty >= qty
        });
    }
    return materials;
}

function renderMilestonesTab(skill) {
    const hasContractor = architectingSkill.hasContractor();
    const milestones = Object.values(MILESTONES);
    const activeMilestone = gameState.getActiveMilestone();

    // Contractor locked banner
    const questNum = findQuestByUnlock('contractor_hired');
    const questLink = questNum
        ? `<span class="quest-link" onclick="window.questModal.navigateToQuest(${questNum})">The Carpenter's Arrival</span>`
        : 'The Carpenter\'s Arrival';

    const contractorBanner = !hasContractor ? `
        <div class="contractor-locked-banner">
            <span class="lock-icon">&#x1F512;</span>
            <div class="lock-text">
                <span class="lock-title">Contractor Required</span>
                <span class="lock-desc">Complete ${questLink} to hire a contractor who can build milestones.</span>
            </div>
        </div>
    ` : '';

    return `
        <div class="milestones-section">
            <div class="section-header">
                <h2 class="section-title">Town Milestones</h2>
                <p class="section-desc">Permanent buildings that unlock features and increase storage</p>
            </div>

            ${contractorBanner}
            ${hasContractor ? renderContractorStatus() : ''}

            <div class="milestones-list">
                ${milestones.map(milestone => {
                    const isCompleted = gameState.hasMilestone(milestone.id);
                    const isActive = activeMilestone?.milestoneId === milestone.id;

                    // Check architecting level requirement
                    const archLocked = skill.level < milestone.level;

                    // Check secondary skill requirement (for tier upgrades)
                    let secondaryLocked = false;
                    let secondarySkillName = '';
                    let secondarySkillLevel = 0;
                    if (milestone.secondarySkill && milestone.secondaryLevel) {
                        const secondarySkill = gameState.getSkill(milestone.secondarySkill);
                        secondarySkillLevel = secondarySkill ? secondarySkill.level : 1;
                        secondaryLocked = secondarySkillLevel < milestone.secondaryLevel;
                        const skillInfo = skillRegistry.get(milestone.secondarySkill);
                        secondarySkillName = skillInfo?.name || milestone.secondarySkill;
                    }

                    const isLocked = archLocked || secondaryLocked || !hasContractor;
                    const canStart = !isCompleted && !isLocked && !activeMilestone;

                    // Check prerequisites (supports both single and array)
                    let prereqMet = true;
                    let missingPrereqs = [];
                    if (milestone.prerequisites && milestone.prerequisites.length > 0) {
                        for (const prereqId of milestone.prerequisites) {
                            if (!gameState.hasMilestone(prereqId)) {
                                prereqMet = false;
                                const prereqMilestone = Object.values(MILESTONES).find(m => m.id === prereqId);
                                missingPrereqs.push(prereqMilestone?.name || prereqId);
                            }
                        }
                    } else if (milestone.prerequisite && !gameState.hasMilestone(milestone.prerequisite)) {
                        prereqMet = false;
                        const prereqMilestone = Object.values(MILESTONES).find(m => m.id === milestone.prerequisite);
                        missingPrereqs.push(prereqMilestone?.name || milestone.prerequisite);
                    }

                    // Calculate progress
                    let progress = 0;
                    if (isActive) {
                        const elapsed = Date.now() - activeMilestone.startedAt;
                        progress = Math.min(elapsed / activeMilestone.duration, 1);
                    }

                    return `
                        <div class="milestone-card ${isCompleted ? 'completed' : ''} ${isActive ? 'active' : ''} ${isLocked ? 'locked' : ''}">
                            <div class="milestone-icon">${milestone.icon}</div>
                            <div class="milestone-info">
                                <div class="milestone-header">
                                    <span class="milestone-name">${milestone.name}</span>
                                    ${archLocked ? `<span class="level-lock">Arch ${milestone.level}</span>` : ''}
                                    ${secondaryLocked ? `<span class="level-lock">${secondarySkillName} ${milestone.secondaryLevel}</span>` : ''}
                                    ${isCompleted ? '<span class="completed-badge">&#x2713;</span>' : ''}
                                </div>
                                <div class="milestone-desc">${milestone.description}</div>

                                ${!isCompleted ? `
                                    <div class="milestone-stats-row">
                                        <div class="milestone-xp">
                                            <span class="stat-icon architecting-icon"></span>
                                            <span class="xp-reward">+${formatNumber(milestone.xp)} XP</span>
                                        </div>
                                        <div class="milestone-build-time">
                                            <span class="stat-icon time-icon"></span>
                                            <span class="build-time-value">${formatTime(milestone.buildTime / 1000)}</span>
                                        </div>
                                    </div>
                                ` : ''}

                                ${!isCompleted ? `
                                    <div class="milestone-requirements">
                                        ${Object.entries(milestone.materials).map(([itemId, qty]) => {
                                            const item = getItem(itemId);
                                            const playerQty = gameState.getItemCount(itemId);
                                            const hasEnough = playerQty >= qty;
                                            return `
                                                <span class="req-item ${hasEnough ? '' : 'missing'}">
                                                    ${item?.name || itemId}: ${playerQty}/${qty}
                                                </span>
                                            `;
                                        }).join('')}
                                        ${milestone.gold > 0 ? `
                                            <span class="req-item ${gameState.getGold() >= milestone.gold ? '' : 'missing'}">
                                                Gold: ${formatWithCommas(gameState.getGold())}/${formatWithCommas(milestone.gold)}
                                            </span>
                                        ` : ''}
                                    </div>
                                ` : ''}

                                ${!prereqMet && !isCompleted ? `
                                    <div class="milestone-prereq">
                                        Requires: ${missingPrereqs.join(', ')}
                                    </div>
                                ` : ''}

                                ${isActive ? `
                                    <div class="milestone-progress">
                                        <div class="progress-bar">
                                            <div class="progress-fill" style="width: ${progress * 100}%"></div>
                                        </div>
                                        <span class="time-remaining">${formatTime((1 - progress) * activeMilestone.duration / 1000)}</span>
                                    </div>
                                ` : ''}
                            </div>

                            ${canStart && prereqMet ? `
                                <button class="milestone-button"
                                        onclick="window.architectingUI.startMilestone('${milestone.id}')">
                                    Build
                                </button>
                            ` : ''}
                        </div>
                    `;
                }).join('')}
            </div>
        </div>
    `;
}

// ==================== UI Actions ====================

export function toggleBlueprint(blueprintId) {
    const activeBlueprints = gameState.getActiveBlueprints();
    const hasDualTalent = getTalentFlatBonus(SKILLS.ARCHITECTING, 'dualBlueprint') > 0;

    // Check if this blueprint is already active
    const activeSlotIndex = activeBlueprints.findIndex(ab => ab.blueprintId === blueprintId);

    if (activeSlotIndex >= 0) {
        // Stopping an active blueprint - show confirmation
        showBlueprintStopConfirm(blueprintId, activeSlotIndex);
    } else if (activeBlueprints.length === 0) {
        // No active blueprints - start directly
        const result = architectingSkill.startBuilding(blueprintId);
        if (!result.success) {
            toastWarning(result.reason);
        }
        renderArchitectingUI();
    } else if (hasDualTalent && activeBlueprints.length < 2) {
        // Dual talent active and a free slot - start as second blueprint
        const result = architectingSkill.startBuilding(blueprintId);
        if (!result.success) {
            toastWarning(result.reason);
        }
        renderArchitectingUI();
    } else if (hasDualTalent && activeBlueprints.length === 2) {
        // Both slots full with dual talent - let player choose which to replace
        showDualSwitchConfirm(blueprintId, activeBlueprints);
    } else {
        // No dual talent - show switch confirmation for slot 0
        showBlueprintSwitchConfirm(blueprintId, activeBlueprints[0].blueprintId, 0);
    }
}

/**
 * Show confirmation modal when stopping a blueprint
 * @param {string} blueprintId - Blueprint to stop
 * @param {number} slotIndex - Which slot (0 or 1)
 */
function showBlueprintStopConfirm(blueprintId, slotIndex = 0) {
    const blueprint = Object.values(BLUEPRINTS).find(b => b.id === blueprintId);
    const progress = gameState.getBlueprintProgress(slotIndex);
    const progressPercent = Math.round(progress * 100);

    const modal = document.createElement('div');
    modal.className = 'blueprint-confirm-modal';
    modal.innerHTML = `
        <div class="blueprint-confirm-content">
            <div class="confirm-header">
                <h3>Stop Building?</h3>
                <button class="confirm-close-btn" onclick="this.closest('.blueprint-confirm-modal').remove();">✕</button>
            </div>
            <div class="confirm-blueprint-info">
                <span class="blueprint-name">${blueprint?.name || 'Blueprint'}</span>
                <div class="confirm-progress">
                    <div class="confirm-progress-bar">
                        <div class="confirm-progress-fill" style="width: ${progressPercent}%"></div>
                    </div>
                    <span class="confirm-progress-text">${progressPercent}% complete</span>
                </div>
            </div>
            <p class="confirm-warning">Progress will be lost if you stop now.</p>
            <div class="confirm-actions">
                <button class="confirm-cancel-btn" onclick="this.closest('.blueprint-confirm-modal').remove();">
                    Keep Building
                </button>
                <button class="confirm-stop-btn" onclick="window.architectingUI.confirmStopBlueprint(${slotIndex}); this.closest('.blueprint-confirm-modal').remove();">
                    Stop Building
                </button>
            </div>
        </div>
    `;
    document.body.appendChild(modal);

    // Close on background click
    modal.addEventListener('click', (e) => {
        if (e.target === modal) modal.remove();
    });
}

/**
 * Show confirmation modal when switching to a different blueprint
 * @param {string} newBlueprintId - New blueprint to build
 * @param {string} currentBlueprintId - Blueprint to replace
 * @param {number} slotIndex - Which slot to replace (0 or 1)
 */
function showBlueprintSwitchConfirm(newBlueprintId, currentBlueprintId, slotIndex = 0) {
    const newBlueprint = Object.values(BLUEPRINTS).find(b => b.id === newBlueprintId);
    const currentBlueprint = Object.values(BLUEPRINTS).find(b => b.id === currentBlueprintId);
    const progress = gameState.getBlueprintProgress(slotIndex);
    const progressPercent = Math.round(progress * 100);

    const modal = document.createElement('div');
    modal.className = 'blueprint-confirm-modal';
    modal.innerHTML = `
        <div class="blueprint-confirm-content">
            <div class="confirm-header">
                <h3>Switch Blueprint?</h3>
                <button class="confirm-close-btn" onclick="this.closest('.blueprint-confirm-modal').remove();">✕</button>
            </div>
            <div class="confirm-blueprint-info current">
                <span class="blueprint-label">Currently Building:</span>
                <span class="blueprint-name">${currentBlueprint?.name || 'Blueprint'}</span>
                <div class="confirm-progress">
                    <div class="confirm-progress-bar">
                        <div class="confirm-progress-fill" style="width: ${progressPercent}%"></div>
                    </div>
                    <span class="confirm-progress-text">${progressPercent}% complete</span>
                </div>
            </div>
            <div class="confirm-blueprint-info new">
                <span class="blueprint-label">Switch to:</span>
                <span class="blueprint-name">${newBlueprint?.name || 'Blueprint'}</span>
            </div>
            <p class="confirm-warning">Current progress will be lost!</p>
            <div class="confirm-actions">
                <button class="confirm-cancel-btn" onclick="this.closest('.blueprint-confirm-modal').remove();">
                    Keep Current
                </button>
                <button class="confirm-switch-btn" onclick="window.architectingUI.confirmSwitchBlueprint('${newBlueprintId}', ${slotIndex}); this.closest('.blueprint-confirm-modal').remove();">
                    Switch Blueprint
                </button>
            </div>
        </div>
    `;
    document.body.appendChild(modal);

    // Close on background click
    modal.addEventListener('click', (e) => {
        if (e.target === modal) modal.remove();
    });
}

/**
 * Show confirmation modal when both dual slots are full - player picks which to replace
 * @param {string} newBlueprintId - New blueprint to build
 * @param {Array} activeBlueprints - Current active blueprints array
 */
function showDualSwitchConfirm(newBlueprintId, activeBlueprints) {
    const newBlueprint = Object.values(BLUEPRINTS).find(b => b.id === newBlueprintId);

    const slotRows = activeBlueprints.map((ab, index) => {
        const bp = Object.values(BLUEPRINTS).find(b => b.id === ab.blueprintId);
        const progress = gameState.getBlueprintProgress(index);
        const progressPercent = Math.round(progress * 100);
        return `
            <div class="dual-switch-slot">
                <div class="dual-switch-slot-info">
                    <span class="slot-label">Slot ${index + 1}:</span>
                    <span class="blueprint-name">${bp?.name || 'Blueprint'}</span>
                    <div class="confirm-progress">
                        <div class="confirm-progress-bar">
                            <div class="confirm-progress-fill" style="width: ${progressPercent}%"></div>
                        </div>
                        <span class="confirm-progress-text">${progressPercent}%</span>
                    </div>
                </div>
                <button class="confirm-switch-btn"
                        onclick="window.architectingUI.confirmSwitchBlueprint('${newBlueprintId}', ${index}); this.closest('.blueprint-confirm-modal').remove();">
                    Replace
                </button>
            </div>
        `;
    }).join('');

    const modal = document.createElement('div');
    modal.className = 'blueprint-confirm-modal';
    modal.innerHTML = `
        <div class="blueprint-confirm-content">
            <div class="confirm-header">
                <h3>Both Slots Full</h3>
                <button class="confirm-close-btn" onclick="this.closest('.blueprint-confirm-modal').remove();">&#x2715;</button>
            </div>
            <div class="confirm-blueprint-info new">
                <span class="blueprint-label">Build:</span>
                <span class="blueprint-name">${newBlueprint?.name || 'Blueprint'}</span>
            </div>
            <p class="confirm-warning">Choose which blueprint to replace:</p>
            <div class="dual-switch-slots">
                ${slotRows}
            </div>
            <div class="confirm-actions">
                <button class="confirm-cancel-btn" onclick="this.closest('.blueprint-confirm-modal').remove();">
                    Cancel
                </button>
            </div>
        </div>
    `;
    document.body.appendChild(modal);

    modal.addEventListener('click', (e) => {
        if (e.target === modal) modal.remove();
    });
}

/**
 * Confirm stopping a blueprint at the given slot
 */
export function confirmStopBlueprint(slotIndex = 0) {
    architectingSkill.stopBuilding(slotIndex);
    renderArchitectingUI();
}

/**
 * Confirm switching to a new blueprint (stops the given slot first)
 */
export function confirmSwitchBlueprint(newBlueprintId, slotIndex = 0) {
    architectingSkill.stopBuilding(slotIndex);
    const result = architectingSkill.startBuilding(newBlueprintId);
    if (!result.success) {
        toastWarning(result.reason);
    }
    renderArchitectingUI();
}

/**
 * Toggle auto-queue on/off
 */
export function toggleAutoQueue() {
    const current = gameState.getAutoQueueEnabled();
    gameState.setAutoQueueEnabled(!current);
    renderArchitectingUI();
}

export function startQuickBuild(blueprintId) {
    const result = architectingSkill.startQuickBuild(blueprintId);
    if (!result.success) {
        toastWarning(result.reason);
    }
    renderArchitectingUI();
}

export function stopQuickBuild() {
    gameState.clearAction();
    renderArchitectingUI();
}

export function upgradeTool(toolId) {
    const result = architectingSkill.upgradeTool(toolId);

    if (result.success) {
        renderArchitectingUI();
    } else {
        toastWarning(result.reason);
    }
}

export function startMilestone(milestoneId) {
    // Check if we need confirmation (single slot with blueprint running)
    const usedSlots = architectingSkill.getUsedContractorSlots();
    const maxSlots = architectingSkill.getContractorSlotCount();
    const activeBlueprints = gameState.getActiveBlueprints();

    if (usedSlots >= maxSlots && activeBlueprints.length > 0 && maxSlots === 1) {
        // Single contractor slot occupied by blueprint - show confirmation
        showMilestoneConfirm(milestoneId, activeBlueprints[0]);
        return;
    }

    const result = architectingSkill.startMilestone(milestoneId);

    if (result.success) {
        toastSuccess('Started building milestone!');
        renderArchitectingUI();
    } else {
        toastWarning(result.reason);
    }
}

/**
 * Show confirmation when starting a milestone will stop an active blueprint
 */
function showMilestoneConfirm(milestoneId, activeBlueprint) {
    const milestone = Object.values(MILESTONES).find(m => m.id === milestoneId);
    const blueprint = Object.values(BLUEPRINTS).find(b => b.id === activeBlueprint.blueprintId);
    const progress = gameState.getBlueprintProgress(0);
    const progressPercent = Math.round(progress * 100);

    const modal = document.createElement('div');
    modal.className = 'blueprint-confirm-modal';
    modal.innerHTML = `
        <div class="blueprint-confirm-content">
            <div class="confirm-header">
                <h3>Stop Blueprint?</h3>
                <button class="confirm-close-btn" onclick="this.closest('.blueprint-confirm-modal').remove();">&#x2715;</button>
            </div>
            <div class="confirm-blueprint-info current">
                <span class="blueprint-label">Currently Building:</span>
                <span class="blueprint-name">${blueprint?.name || 'Blueprint'}</span>
                <div class="confirm-progress">
                    <div class="confirm-progress-bar">
                        <div class="confirm-progress-fill" style="width: ${progressPercent}%"></div>
                    </div>
                    <span class="confirm-progress-text">${progressPercent}% complete</span>
                </div>
            </div>
            <div class="confirm-blueprint-info new">
                <span class="blueprint-label">Start Milestone:</span>
                <span class="blueprint-name">${milestone?.icon || ''} ${milestone?.name || 'Milestone'}</span>
            </div>
            <p class="confirm-warning">Your contractor will stop building ${blueprint?.name || 'the blueprint'} to work on this milestone. Blueprint progress will be lost.</p>
            <div class="confirm-actions">
                <button class="confirm-cancel-btn" onclick="this.closest('.blueprint-confirm-modal').remove();">
                    Keep Blueprint
                </button>
                <button class="confirm-switch-btn" onclick="window.architectingUI.confirmMilestoneSwitch('${milestoneId}'); this.closest('.blueprint-confirm-modal').remove();">
                    Start Milestone
                </button>
            </div>
        </div>
    `;
    document.body.appendChild(modal);

    modal.addEventListener('click', (e) => {
        if (e.target === modal) modal.remove();
    });
}

/**
 * Confirm switching from blueprint to milestone (stops blueprint first)
 */
export function confirmMilestoneSwitch(milestoneId) {
    // Stop the active blueprint
    architectingSkill.stopBuilding(0);

    // Start the milestone
    const result = architectingSkill.startMilestone(milestoneId);
    if (result.success) {
        toastSuccess('Started building milestone!');
    } else {
        toastWarning(result.reason);
    }
    renderArchitectingUI();
}

export function startCivicContract(contractId) {
    const result = architectingSkill.startCivicContract(contractId);

    if (result.success) {
        toastSuccess('Accepted civic contract!');
        renderArchitectingUI();
    } else {
        toastWarning(result.reason);
    }
}

export function startNextPhase() {
    const result = architectingSkill.startNextPhase();

    if (result.success) {
        toastSuccess('Started next phase!');
        renderArchitectingUI();
    } else {
        toastWarning(result.reason);
    }
}

export function updateArchitectingUI() {
    // Update compact header stats
    const skill = gameState.getSkill(SKILLS.ARCHITECTING);
    const xpPerHour = getXPPerHour(SKILLS.ARCHITECTING);

    // Update XP total
    const xpTotalEl = document.querySelector('.architecting-interface .compact-stat.xp-total .stat-value');
    if (xpTotalEl) xpTotalEl.textContent = formatWithCommas(skill.xp);

    // Update XP rate
    const xpRateEl = document.querySelector('.architecting-interface .compact-stat.xp-rate .stat-value');
    if (xpRateEl) xpRateEl.textContent = formatWithCommas(xpPerHour);

    // Update progress bar
    const isMax = skill.level >= GAME_CONFIG.MAX_LEVEL;
    const virtualEnabled = gameState.settings.virtualLevelsEnabled;
    const isVirtualMax = skill.virtualLevel >= GAME_CONFIG.VIRTUAL_LEVEL_CAP;

    let progressPercent, progressText;
    if (isMax && virtualEnabled) {
        progressPercent = isVirtualMax ? 100 : (skill.currentXp / skill.nextLevelXp * 100);
        progressText = isVirtualMax ? 'Virtual Max' : `${formatWithCommas(skill.currentXp)} / ${formatWithCommas(skill.nextLevelXp)}`;
    } else if (isMax) {
        progressPercent = 100;
        progressText = 'Mastered';
    } else {
        progressPercent = (skill.currentXp / skill.nextLevelXp * 100);
        progressText = `${formatWithCommas(skill.currentXp)} / ${formatWithCommas(skill.nextLevelXp)}`;
    }

    const progressFill = document.querySelector('.architecting-interface .compact-progress-fill');
    if (progressFill) progressFill.style.width = `${progressPercent}%`;

    const progressPercentEl = document.querySelector('.architecting-interface .compact-progress-percent');
    if (progressPercentEl) progressPercentEl.textContent = `${Math.floor(progressPercent)}%`;

    const progressTextEl = document.querySelector('.architecting-interface .compact-progress-text');
    if (progressTextEl) progressTextEl.textContent = `${progressText} XP`;

    // Update active quick build progress (pre-contractor view)
    const currentAction = gameState.player.currentAction;
    if (currentAction?.type === 'architecting' && currentAction?.buildMode === 'active' && currentTab === 'blueprints') {
        const quickBuildCard = document.querySelector('.quick-build-card');
        if (quickBuildCard) {
            const progressFillEl = quickBuildCard.querySelector('.blueprint-progress-fill');
            if (progressFillEl) {
                progressFillEl.style.width = `${(currentAction.progress || 0) * 100}%`;
            }
        }
    }

    // Check for stale active cards (blueprint completed but UI still shows active)
    const activeBlueprints = gameState.getActiveBlueprints();
    const existingActiveCards = document.querySelectorAll('.architecting-interface .blueprint-card.active');
    if (existingActiveCards.length > activeBlueprints.length) {
        // Blueprint was completed, re-render to clear active state
        renderArchitectingUI();
        return;
    }

    // Update blueprint progress for all active blueprints
    if (activeBlueprints.length > 0 && currentTab === 'blueprints') {
        for (let i = 0; i < activeBlueprints.length; i++) {
            const ab = activeBlueprints[i];
            const progress = gameState.getBlueprintProgress(i);
            const activeCard = document.querySelector(`.blueprint-card[data-blueprint-id="${ab.blueprintId}"]`);
            if (activeCard) {
                const progressFillEl = activeCard.querySelector('.blueprint-progress .blueprint-progress-fill');
                if (progressFillEl) {
                    progressFillEl.style.width = `${progress * 100}%`;
                }
                const timeRemainingEl = activeCard.querySelector('.blueprint-timer .time-remaining');
                if (timeRemainingEl) {
                    const remaining = (1 - progress) * ab.duration / 1000;
                    timeRemainingEl.textContent = formatTime(remaining);
                }
            }
        }
    }

    // Update milestone progress
    const activeMilestone = gameState.getActiveMilestone();
    if (activeMilestone && currentTab === 'milestones') {
        const milestoneProgress = architectingSkill.getMilestoneProgress();
        if (milestoneProgress) {
            const progressFill = document.querySelector('.milestone-card.active .progress-fill');
            if (progressFill) {
                progressFill.style.width = `${milestoneProgress.progress * 100}%`;
            }
            const timeRemaining = document.querySelector('.milestone-card.active .time-remaining');
            if (timeRemaining) {
                const remaining = (1 - milestoneProgress.progress) * activeMilestone.duration / 1000;
                timeRemaining.textContent = formatTime(remaining);
            }
        }
    }

    // Update material counts on blueprint cards
    updateMaterialCounts();
}

function updateMaterialCounts() {
    const cards = document.querySelectorAll('.blueprint-card');

    cards.forEach(card => {
        const blueprintId = card.dataset.blueprintId;
        if (!blueprintId) return;

        const blueprint = Object.values(BLUEPRINTS).find(b => b.id === blueprintId);
        if (!blueprint) return;

        const materialRows = card.querySelectorAll('.material-row');
        let allHave = true;

        materialRows.forEach((row, index) => {
            const countEl = row.querySelector('.material-count');
            if (!countEl) return;

            const materials = Object.entries(blueprint.materials);
            if (index < materials.length) {
                const [itemId, qty] = materials[index];
                const playerQty = gameState.getItemCount(itemId);
                countEl.textContent = `${playerQty}/${qty}`;

                const hasEnough = playerQty >= qty;
                row.classList.toggle('missing', !hasEnough);
                if (!hasEnough) allHave = false;
            } else if (blueprint.gold > 0) {
                // Gold row
                const playerGold = gameState.getGold();
                countEl.textContent = `${formatWithCommas(playerGold)}/${formatWithCommas(blueprint.gold)}`;
                const hasEnough = playerGold >= blueprint.gold;
                row.classList.toggle('missing', !hasEnough);
                if (!hasEnough) allHave = false;
            }
        });

        card.classList.toggle('no-materials', !allHave && !card.classList.contains('locked'));
    });
}

export function initArchitectingUI() {
    architectingSkill.initializeStartingTool();

    architectingSkill.onBlueprintComplete = (blueprint, xpResult) => {
        if (xpResult.leveledUp) {
            toastSuccess(`Architecting level ${xpResult.newLevel}!`);
        }

        if (getCurrentSkillId() === SKILLS.ARCHITECTING && !isTalentTreeOpen()) {
            const speedMult = IS_DEV ? adminPanel.state.global.speedMultiplier : 1;
            if (speedMult <= 1) {
                renderArchitectingUI();
            }
        }
    };

    architectingSkill.onMilestoneComplete = (milestone, xpResult) => {
        toastSuccess(`Completed ${milestone.name}!`);
        if (getCurrentSkillId() === SKILLS.ARCHITECTING && !isTalentTreeOpen()) {
            renderArchitectingUI();
        }
    };

    architectingSkill.onCivicPhaseComplete = (contract, phase, phaseIndex) => {
        if (phase.type === 'wait') {
            toastSuccess(`${contract.name}: Wait phase complete!`);
        } else {
            toastSuccess(`${contract.name}: ${phase.name} complete!`);
        }
        if (getCurrentSkillId() === SKILLS.ARCHITECTING && !isTalentTreeOpen()) {
            renderArchitectingUI();
        }
    };

    architectingSkill.onQuickBuildComplete = (blueprint, xpResult) => {
        if (xpResult.leveledUp) {
            toastSuccess(`Architecting level ${xpResult.newLevel}!`);
        }

        if (getCurrentSkillId() === SKILLS.ARCHITECTING && !isTalentTreeOpen()) {
            const speedMult = IS_DEV ? adminPanel.state.global.speedMultiplier : 1;
            if (speedMult <= 1) {
                renderArchitectingUI();
            }
        }
    };
}

if (typeof window !== 'undefined') {
    window.architectingUI = {
        toggleBlueprint,
        upgradeTool,
        switchTab,
        switchBlueprintTier,
        startMilestone,
        startQuickBuild,
        stopQuickBuild,
        startCivicContract,
        startNextPhase,
        confirmStopBlueprint,
        confirmSwitchBlueprint,
        confirmMilestoneSwitch,
        toggleAutoQueue
    };
}

export default {
    renderArchitectingUI,
    toggleBlueprint,
    upgradeTool,
    updateArchitectingUI,
    initArchitectingUI,
    switchTab,
    switchBlueprintTier,
    getCurrentTab,
    startMilestone,
    startQuickBuild,
    startCivicContract,
    startNextPhase,
    confirmMilestoneSwitch
};
